import { getCurrentDomain } from 'common/currentDomain';
import { type ScheduleSendValues } from './types/scheduleSend';
import { type AttachmentConfiguration, type PdfFormat } from './types/attachment';
import { camelCase, invert, isEmpty, snakeCase } from 'lodash';
import {
  type DatesInfo,
  type DayTypes,
  dayTypesDict,
  type FrequencyConfig,
  type IntervalInfo,
  type IntervalTypes,
  intervalTypesDict,
  type TimeInfo,
  timezoneDict,
} from 'common/types/frequencySelector/frequencySelector';
import moment from 'moment-timezone';
import { mappedTimezone, reverseMappedTimezone } from '../FrequencySelector/helpers/timezoneHelper';
import { defaultDateFormat } from '../FrequencySelector/constants/dateFormats';
import { type CatalogInteractiveUser } from 'common/types/users/catalogUsers';
import type InteractiveUser from 'common/types/users/interactiveUser';
import {
  type ScheduleDeliverySubscriptionWithType,
  type ScreenshotSubscriptionWithType,
  type SubscriptionWithType,
} from './types/subscription';
import { DATE_FORMAT } from 'common/dates';
import { buildDefaultTableName, sortComponents } from './components/Attachment/helpers';
import { DEFAULT_ATTACHMENT_CONFIG } from './constants';

//Map Scheduled Delivery Configuration to Request Payload
export const convertDateTimeToUTC = (date: string | undefined, timeInfo: TimeInfo) => {
  if (!date || !timeInfo || !timeInfo.time) {
    return '';
  }
  const dateTime = `${date} ${timeInfo.time}`;
  return moment.tz(dateTime, 'MM/DD/YYYY HH:mm', mappedTimezone(timeInfo.timeZone)).utc().format();
};

export const convertDays = (days: DayTypes[] | undefined) => {
  if (!days) {
    return [];
  }
  return days.map((day) => dayTypesDict[day].id).sort((a, b) => a - b);
};

export const buildFrequencyPayload = (config: FrequencyConfig) => {
  const { days: daysList, count, type, week } = config.intervalInfo;
  const { startDate, endDate, hasEndDate } = config.datesInfo;

  return {
    first_occurrence: convertDateTimeToUTC(startDate, config.timeInfo),
    first_occurrence_timezone: timezoneDict[config.timeInfo.timeZone],
    recurrence: {
      type: intervalTypesDict[type],
      interval: count,
      ...((type === 'week' || type === 'month') && { days: convertDays(daysList) }),
      ...(type === 'month' && {
        repeat_on: week,
      }),
    },
    last_occurrence: hasEndDate && endDate ? moment(new Date(endDate)).format(DATE_FORMAT) : null,
  };
};

export const buildPayload = (scheduledSendValues: ScheduleSendValues, currentUser: InteractiveUser) => {
  return {
    domain_cname: getCurrentDomain(),
    subscriber_uid: getRecipientsUid(scheduledSendValues),
    asset_uid: window.STORY_DATA.uid,
    schedule_name: scheduledSendValues.name,
    subject_line: scheduledSendValues.subject,
    subscribed_by_uid: currentUser.id,
    ...buildAttachmentPayload(scheduledSendValues.attachmentConfigs),
    ...buildFrequencyPayload(scheduledSendValues.frequencyConfig),
  };
};

export const convertCamelCasePdfOptionKeysToSnakeCase = (pdfOptions: PdfFormat) => {
  const result = Object.entries(pdfOptions as Record<string, unknown>).reduce(
    (acc, [key, value]) => {
      if (value !== undefined) {
        const snakeKey = snakeCase(key);
        acc[snakeKey] = value;
      }
      return acc;
    },
    {} as Record<string, unknown>,
  );

  return result;
};

const buildAttachmentPayload = (attachmentConfigs: AttachmentConfiguration[]) => {
  const attachments = attachmentConfigs.map((attachment) => {
    if (attachment.type === 'full_report') {
      return { type: attachment.type };
    } else {
      return {
        component_uid: attachment.componentUuid,
        type: attachment.type,
        format: attachment.format,
        ...(attachment.pdfOptions && {
          pdf_options: convertCamelCasePdfOptionKeysToSnakeCase(attachment.pdfOptions),
        }),
        is_table_supported_for_pdf_export: attachment.isTableSupportedForPdfExport,
      };
    }
  });

  return {
    attachments: attachments,
  };
};

export const getRecipientsUid = (scheduledSendValues: ScheduleSendValues) => {
  return scheduledSendValues.recipients.map((recipient) => recipient.value);
};

//Map Request Response to Scheduled Delivery Configuration
export const buildRecipientsFromSubscriptions = (
  subscriptions: SubscriptionWithType[],
  recipients: CatalogInteractiveUser[],
) => {
  return subscriptions.map((subscription) => ({
    label: recipients.find((recipient) => recipient.id === subscription.subscriber_uid)?.email || '',
    value: subscription.subscriber_uid,
  }));
};

const buildComponentTitleMap = (storyData: any): Map<string, string> => {
  const componentTitles = new Map<string, string>();
  let tableCount = 0;

  storyData.blocks.forEach((block: any) => {
    const components = storyData.isFlexibleStory ? sortComponents(block.components) : block.components;

    components.forEach((component: any) => {
      if (component.type === 'socrata.visualization.agTable') {
        tableCount++;
        const title = component.value?.vif?.title;
        componentTitles.set(
          component.componentUuid,
          isEmpty(title) ? buildDefaultTableName(tableCount) : title,
        );
      }
    });
  });

  return componentTitles;
};

export const buildAttachmentsConfigFromSubscription = (
  subscription: ScheduleDeliverySubscriptionWithType,
) => {
  if (!subscription.attachments?.length) {
    return [];
  }

  const storyData = window.STORY_DATA;
  const componentTitles = buildComponentTitleMap(storyData);

  //filter attachments to those that are only present on story data json
  //and map the attachment values accordingly with the name value referencing the component's title on the story data json
  return subscription.attachments
    .filter((attachment) => {
      return (
        attachment.type === 'full_report' ||
        (attachment.component_uid && componentTitles.has(attachment.component_uid))
      );
    })
    .map((attachment) => {
      if (attachment.type === 'full_report') {
        return {
          format: DEFAULT_ATTACHMENT_CONFIG.format,
          name: DEFAULT_ATTACHMENT_CONFIG.name,
          type: attachment.type,
          value: DEFAULT_ATTACHMENT_CONFIG.value,
        };
      } else {
        const title = attachment.component_uid ? componentTitles.get(attachment.component_uid) : '';
        return {
          value: attachment.component_uid ?? '',
          componentUuid: attachment.component_uid,
          format: attachment.format ?? '',
          type: attachment.type,
          name: title ?? '',
          ...(attachment.pdf_options && {
            pdfOptions: convertSnakeCasePdfOptionKeysToCamelCase(attachment.pdf_options) as PdfFormat,
          }),
          isTableSupportedForPdfExport: attachment.is_table_supported_for_pdf_export,
        };
      }
    });
};

//Configuration from Watch
export const buildWatchFrequencyConfigFromSubscription = (subscription: ScreenshotSubscriptionWithType) => {
  const dateTime = moment.tz(subscription.first_occurrence, subscription.first_occurrence_timezone || '');
  const startDate = dateTime.format(defaultDateFormat);
  const time = dateTime.format('HH:mm');
  const frequencyConfig: FrequencyConfig = {
    intervalInfo: {
      count: subscription.interval?.days,
      type: 'day' as IntervalTypes,
    },
    timeInfo: {
      timeZone: reverseMappedTimezone(subscription.first_occurrence_timezone),
      time,
    },
    datesInfo: {
      startDate,
      hasEndDate: false,
    },
  };

  return frequencyConfig;
};

//Configuration from Scheduled Delivery
export const buildScheduledDeliveryFrequencyConfigFromSubscription = (
  subscription: ScheduleDeliverySubscriptionWithType,
) => {
  const dateTime = moment.tz(subscription.first_occurrence, subscription.first_occurrence_timezone || '');
  const startDate = dateTime.format(defaultDateFormat);
  const time = dateTime.format('HH:mm');

  const intervalInfo: IntervalInfo = {
    count: subscription.recurrence.interval,
    type: reverseMappedType(subscription.recurrence.type),
    ...(subscription.recurrence.days && { days: reverseMappedDays(subscription.recurrence.days) }),
    ...(subscription.recurrence.repeat_on && { week: subscription.recurrence.repeat_on }),
  };

  const timeInfo: TimeInfo = {
    time,
    timeZone: reverseMappedTimezone(subscription.first_occurrence_timezone),
  };

  const datesInfo: DatesInfo = {
    startDate,
    ...(subscription.last_occurrence
      ? {
          hasEndDate: true,
          endDate: moment(subscription.last_occurrence, DATE_FORMAT, true).format(defaultDateFormat),
        }
      : { hasEndDate: false }),
  };

  const frequencyConfig: FrequencyConfig = {
    intervalInfo,
    timeInfo,
    datesInfo,
  };

  return frequencyConfig;
};

export const getName = (subscription: SubscriptionWithType) => {
  if ('schedule_name' in subscription && subscription.schedule_name) {
    return subscription.schedule_name;
  }

  return '';
};

export const getSubject = (subscription: SubscriptionWithType) => {
  if ('subject_line' in subscription && subscription.subject_line) {
    return subscription.subject_line;
  }

  return '';
};

export const reverseMappedDays = (days: number[]) => {
  const daysList: DayTypes[] = [];
  Object.entries(dayTypesDict).forEach(([key, value]) => {
    if (days.includes(value.id)) {
      daysList.push(key as DayTypes);
    }
  });
  return daysList;
};

const invertedType = invert(intervalTypesDict) as Record<string, IntervalTypes>;

export const reverseMappedType = (type: string) => invertedType[type];

export const convertSnakeCasePdfOptionKeysToCamelCase = (pdfOptions: Record<string, unknown>) => {
  const convertedPdfOptions = Object.entries(pdfOptions).reduce(
    (acc, [key, value]) => {
      if (value !== undefined) {
        const camelCaseKey = camelCase(key);
        acc[camelCaseKey] = value;
      }
      return acc;
    },
    {} as Record<string, unknown>,
  );

  return convertedPdfOptions;
};
